import { useState, useEffect } from "react";
import { getStats } from "../../api/admin";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Users, CheckCircle, XCircle, ShieldCheck, Database, Plugs } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "../../contexts/AuthContext";
import { isSuperAdminRole } from "../../utils/roles";

/**
 * AdminDashboardPage - Overview stats and recent activity
 */
export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStats();
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
      setError(err?.response?.data?.detail || "Failed to load dashboard stats");
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-dashboard">
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-6">
          Dashboard
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <Skeleton className="h-5 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="admin-dashboard">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm">
          {error}
        </div>
      </div>
    );
  }

  const { users, system, recent_activity } = stats;
  const isSuperAdmin = isSuperAdminRole(user?.role);

  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-1">
        Dashboard
      </h2>
      <p className="text-sm text-slate-500 mb-6" data-testid="dashboard-scope-label">
        {isSuperAdmin
          ? "System-wide view across all organizations"
          : `Showing data for ${user?.organization_name || "your organization"} only`}
      </p>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Users"
          value={users.total}
          icon={<Users size={24} className="text-slate-600" />}
          testId="stat-total-users"
        />
        <StatCard
          title="Active Users"
          value={users.active}
          icon={<CheckCircle size={24} className="text-green-600" />}
          testId="stat-active-users"
        />
        <StatCard
          title="Revoked / Needs Attention"
          value={users.revoked}
          icon={<XCircle size={24} className="text-red-600" />}
          testId="stat-revoked-users"
        />
        <StatCard
          title="Admins"
          value={users.admins}
          icon={<ShieldCheck size={24} className="text-blue-600" />}
          testId="stat-admins"
        />
      </div>

      {/* System Health */}
      <Card className="border-slate-200 rounded-sm mb-6" data-testid="system-health-card">
        <CardHeader>
          <CardTitle className="font-cabinet text-lg font-bold flex items-center gap-2">
            <Database size={20} />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  system.environment ? "bg-green-500" : "bg-red-500"
                }`}
              />
              <span className="text-sm text-slate-700">
                <span className="font-semibold">Environment:</span> {system.environment || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  system.backend ? "bg-green-500" : "bg-red-500"
                }`}
              />
              <span className="text-sm text-slate-700">
                <span className="font-semibold">Backend:</span> {system.backend || "Unknown"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  system.db_connected ? "bg-green-500" : "bg-red-500"
                }`}
              />
              <span className="text-sm text-slate-700">
                <span className="font-semibold">Database:</span>{" "}
                {system.db_connected ? "Connected" : "Disconnected"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integration Health (org_admin only) */}
      {stats.integrations && (
        <Card className="border-slate-200 rounded-sm mb-6" data-testid="integration-health-card">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg font-bold flex items-center gap-2">
              <Plugs size={20} />
              Integration Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm">
                <div className={`w-3 h-3 rounded-full ${stats.integrations.onedrive?.connected ? "bg-green-500" : "bg-red-500"}`} />
                <div>
                  <p className="text-sm font-semibold text-slate-700">OneDrive</p>
                  <p className="text-xs text-slate-500">
                    {stats.integrations.onedrive?.connected ? "Connected" : "Not connected"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm">
                <div className={`w-3 h-3 rounded-full ${
                  stats.integrations.email?.active ? "bg-green-500" : stats.integrations.email?.last_received ? "bg-amber-500" : "bg-red-500"
                }`} />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Email Routing</p>
                  <p className="text-xs text-slate-500">
                    {stats.integrations.email?.active
                      ? `Active${stats.integrations.email?.last_received ? ` — last: ${formatTimestamp(stats.integrations.email.last_received)}` : ""}`
                      : "No recent activity"}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card className="border-slate-200 rounded-sm" data-testid="recent-activity-list">
        <CardHeader>
          <CardTitle className="font-cabinet text-lg font-bold">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {!recent_activity || recent_activity.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No activity yet</p>
          ) : (
            <div className="space-y-3">
              {recent_activity.slice(0, 10).map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-slate-900">
                        {activity.action}
                      </span>
                      <span className="text-xs text-slate-500">
                        {activity.user_email}
                      </span>
                    </div>
                    {activity.detail && (
                      <p className="text-sm text-slate-600">{activity.detail}</p>
                    )}
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {formatTimestamp(activity.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, testId }) {
  return (
    <Card className="border-slate-200 rounded-sm" data-testid={testId}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-600">
            {title}
          </CardTitle>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-cabinet font-black text-slate-900">
          {value}
        </div>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function formatTimestamp(timestamp) {
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch {
    return timestamp;
  }
}
